import {
  Box,
  Button,
  HStack,
  VStack,
  Text,
  Select,
  Input,
  Collapse,
  Slider,
  SliderTrack,
  SliderFilledTrack,
  SliderThumb
} from "@chakra-ui/react";
import { observer } from "mobx-react-lite";
import { useStores } from "../../store/store_context";
import DatePicker from "react-datepicker";
import { ru } from "date-fns/locale";
import {
  startOfWeek,
  endOfWeek,
  startOfMonth,
  endOfMonth,
  startOfQuarter,
  endOfQuarter,
  startOfYear,
  endOfYear,
  subMonths,
  subYears,
  addMonths,
  addYears,
  addWeeks,
} from "date-fns";
import ColumnManager from "../column_manager";
import { INN_COLUMN_KEY } from "../../utils/report_columns";

const DateFilters = observer(() => {
  const { pageStore } = useStores();

  // Компактный вид — пресет видимости колонок (порядок не трогает)
  const selectCompactView = () => {
    const compactCols = [
      "Группа",
      "Номер счета",
      "Выручка в руб.",
      "Оплачено в руб.",
      "% оплаты",
      "Долг в евро",
      "Долг в руб.",
      "% отгрузки"
    ];
    const newColumns = pageStore.columnConfigDebitor.map(c => ({
      ...c,
      active: compactCols.includes(c.title)
    }));
    pageStore.setColumnConfigDebitor(newColumns);
  };

  const getPeriodDates = (periodType) => {
    const now = new Date();
    const lastYear = subYears(now, 1);
    switch (periodType) {
      case 'week':
        return {
          start: startOfWeek(now, { locale: ru }),
          end: endOfWeek(now, { locale: ru })
        };
      case 'nextWeek': {
        const next = addWeeks(now, 1);
        return {
          start: startOfWeek(next, { locale: ru }),
          end: endOfWeek(next, { locale: ru })
        };
      }
      case 'month':
        return {
          start: startOfMonth(now),
          end: endOfMonth(now)
        };
      case 'pastMonth': {
        const lastMonth = subMonths(now, 1);
        return {
          start: startOfMonth(lastMonth),
          end: endOfMonth(lastMonth)
        };
      }
      case 'nextMonth': {
        const next = addMonths(now, 1);
        return {
          start: startOfMonth(next),
          end: endOfMonth(next)
        };
      }
      case 'quarter':
        return {
          start: startOfQuarter(now),
          end: endOfQuarter(now)
        };
      case 'nextQuarter': {
        const next = addMonths(startOfQuarter(now), 3);
        return {
          start: startOfQuarter(next),
          end: endOfQuarter(next)
        };
      }
      case 'halfYear':
        return {
          start: startOfMonth(subMonths(now, 6)),
          end: endOfMonth(now)
        };
      case 'nextHalfYear': {
        const y = now.getFullYear();
        if (now.getMonth() < 6) {
          return {
            start: new Date(y, 6, 1),
            end: endOfMonth(new Date(y, 11, 1))
          };
        }
        return {
          start: new Date(y + 1, 0, 1),
          end: endOfMonth(new Date(y + 1, 5, 1))
        };
      }
      case 'year':
        return {
          start: startOfYear(now),
          end: endOfYear(now)
        };
      case 'pastYear':
        return {
          start: startOfYear(lastYear),
          end: endOfYear(lastYear),
        };
      case 'nextYear': {
        const next = addYears(now, 1);
        return {
          start: startOfYear(next),
          end: endOfYear(next)
        };
      }
      default:
        return { start: null, end: null };
    }
  };

  const handleStartPeriodChange = (periodType) => {
    if (periodType === 'none') {
      pageStore.setOrderStartOpenDateFilter(null);
      pageStore.setOrderEndOpenDateFilter(null);
    } else if (periodType !== 'custom') {
      const dates = getPeriodDates(periodType);
      pageStore.setOrderStartOpenDateFilter(dates.start);
      pageStore.setOrderEndOpenDateFilter(dates.end);
    }
  };

  const handleEndPeriodChange = (periodType) => {
    if (periodType === 'none') {
      pageStore.setOrderStartCloseDateFilter(null);
      pageStore.setOrderEndCloseDateFilter(null);
    } else if (periodType !== 'custom') {
      const dates = getPeriodDates(periodType);
      pageStore.setOrderStartCloseDateFilter(dates.start);
      pageStore.setOrderEndCloseDateFilter(dates.end);
    }
  };

  const handleDzPeriodChange = (periodType) => {
    if (periodType === 'none' || periodType === 'fill' || periodType === 'empty') {
      pageStore.setOrderStartDzDateFilter(null);
      pageStore.setOrderEndDzDateFilter(null);
    } else if (periodType !== 'custom') {
      const dates = getPeriodDates(periodType);
      pageStore.setOrderStartDzDateFilter(dates.start);
      pageStore.setOrderEndDzDateFilter(dates.end);
    }
  };

  const handleCargoReadyPeriodChange = (periodType) => {
    if (periodType === 'none' || periodType === 'fill' || periodType === 'empty') {
      pageStore.setOrderStartCargoReadyFilter(null);
      pageStore.setOrderEndCargoReadyFilter(null);
    } else if (periodType !== 'custom') {
      const dates = getPeriodDates(periodType);
      pageStore.setOrderStartCargoReadyFilter(dates.start);
      pageStore.setOrderEndCargoReadyFilter(dates.end);
    }
  };

  const handleDateAtClientPeriodChange = (periodType) => {
    if (periodType === 'none' || periodType === 'fill' || periodType === 'empty') {
      pageStore.setOrderStartDateAtClientFilter(null);
      pageStore.setOrderEndDateAtClientFilter(null);
    } else if (periodType !== 'custom') {
      const dates = getPeriodDates(periodType);
      pageStore.setOrderStartDateAtClientFilter(dates.start);
      pageStore.setOrderEndDateAtClientFilter(dates.end);
    }
  };

  const handleShipmentDatePeriodChange = (periodType) => {
    if (periodType === 'none' || periodType === 'fill' || periodType === 'empty') {
      pageStore.setOrderStartShipmentDateFilter(null);
      pageStore.setOrderEndShipmentDateFilter(null);
    } else if (periodType !== 'custom') {
      const dates = getPeriodDates(periodType);
      pageStore.setOrderStartShipmentDateFilter(dates.start);
      pageStore.setOrderEndShipmentDateFilter(dates.end);
    }
  };

  const forecastPeriodOptions = (fillLabel, emptyLabel) => (
    <>
      <option style={{ background: "#0C0C0C", color: "#EEEEEE" }} value="none">Без фильтра(все)</option>
      <option style={{ background: "#0C0C0C", color: "#EEEEEE" }} value="fill">{fillLabel}</option>
      <option style={{ background: "#0C0C0C", color: "#EEEEEE" }} value="empty">{emptyLabel}</option>
      <option style={{ background: "#0C0C0C", color: "#EEEEEE" }} value="week">Текущая неделя</option>
      <option style={{ background: "#0C0C0C", color: "#EEEEEE" }} value="nextWeek">Следующая неделя</option>
      <option style={{ background: "#0C0C0C", color: "#EEEEEE" }} value="month">Текущий месяц</option>
      <option style={{ background: "#0C0C0C", color: "#EEEEEE" }} value="nextMonth">Следующий месяц</option>
      <option style={{ background: "#0C0C0C", color: "#EEEEEE" }} value="pastMonth">Прошлый месяц</option>
      <option style={{ background: "#0C0C0C", color: "#EEEEEE" }} value="quarter">Текущий квартал</option>
      <option style={{ background: "#0C0C0C", color: "#EEEEEE" }} value="nextQuarter">Следующий квартал</option>
      <option style={{ background: "#0C0C0C", color: "#EEEEEE" }} value="halfYear">Текущее полугодие</option>
      <option style={{ background: "#0C0C0C", color: "#EEEEEE" }} value="nextHalfYear">Следующее полугодие</option>
      <option style={{ background: "#0C0C0C", color: "#EEEEEE" }} value="year">Текущий год</option>
      <option style={{ background: "#0C0C0C", color: "#EEEEEE" }} value="nextYear">Следующий год</option>
      <option style={{ background: "#0C0C0C", color: "#EEEEEE" }} value="pastYear">Предыдущий год</option>
      <option style={{ background: "#0C0C0C", color: "#EEEEEE" }} value="custom">Произвольный период</option>
    </>
  );

  // «Дата отгрузки» — факт, а не прогноз: будущих значений у неё не бывает,
  // поэтому пресеты «Следующий …» дали бы гарантированно пустой отчёт.
  const factPeriodOptions = (fillLabel, emptyLabel) => (
    <>
      <option style={{ background: "#0C0C0C", color: "#EEEEEE" }} value="none">Без фильтра(все)</option>
      <option style={{ background: "#0C0C0C", color: "#EEEEEE" }} value="fill">{fillLabel}</option>
      <option style={{ background: "#0C0C0C", color: "#EEEEEE" }} value="empty">{emptyLabel}</option>
      <option style={{ background: "#0C0C0C", color: "#EEEEEE" }} value="week">Текущая неделя</option>
      <option style={{ background: "#0C0C0C", color: "#EEEEEE" }} value="month">Текущий месяц</option>
      <option style={{ background: "#0C0C0C", color: "#EEEEEE" }} value="pastMonth">Прошлый месяц</option>
      <option style={{ background: "#0C0C0C", color: "#EEEEEE" }} value="quarter">Текущий квартал</option>
      <option style={{ background: "#0C0C0C", color: "#EEEEEE" }} value="halfYear">Текущее полугодие</option>
      <option style={{ background: "#0C0C0C", color: "#EEEEEE" }} value="year">Текущий год</option>
      <option style={{ background: "#0C0C0C", color: "#EEEEEE" }} value="pastYear">Предыдущий год</option>
      <option style={{ background: "#0C0C0C", color: "#EEEEEE" }} value="custom">Произвольный период</option>
    </>
  );

  const customDatePickerStyle = {
    border: "none",
    background: "#333",
    color: "#EEEEEE",
    borderRadius: "10px",
    fontWeight: 600,
  };

  return (
    <>
      <Collapse in={pageStore.open_filter} animateOpacity style={{ overflow: pageStore.open_filter ? 'visible' : 'hidden' }}>
        <VStack align="stretch" width="100%" gap={3} mb={4}>
        {/* Семь фильтров всегда в одну строку: колонки делят ширину поровну,
            поэтому ряд не переносится ни на широком мониторе, ни на ноутбуке. */}
        <HStack textColor={"white"} width={"100%"} align={"start"} gap={"12px"} flexWrap="nowrap">
          <VStack flex="1" minW={0}>
            <Text fontSize={"20px"}>Номер счёта</Text>
            <Input
              value={pageStore.order_num_filter}
              onChange={(e) => pageStore.setOrderNumFilter(e.target.value)}
              width="100%"
            />
          </VStack>
          <VStack flex="1" minW={0}>
            <Text fontSize={"20px"}>Период открытия счёта</Text>
            {/* Фильтр по дате открытия */}
            <Select
              value={pageStore.order_type_date_start_filter}
              onChange={(e) => {
                pageStore.setOrderTypeDateStartFilter(e.target.value);
                handleStartPeriodChange(e.target.value);
              }}
              width="100%"
            >
              <option style={{ background: "#0C0C0C", color: "#EEEEEE" }} value="none">Без фильтра(все)</option>
              <option style={{ background: "#0C0C0C", color: "#EEEEEE" }} value="fill">Открыт</option>
              <option style={{ background: "#0C0C0C", color: "#EEEEEE" }} value="empty">Не открыт</option>
              <option style={{ background: "#0C0C0C", color: "#EEEEEE" }} value="week">Текущая неделя</option>
              <option style={{ background: "#0C0C0C", color: "#EEEEEE" }} value="month">Текущий месяц</option>
              <option style={{ background: "#0C0C0C", color: "#EEEEEE" }} value="pastMonth">Прошлый месяц</option>
              <option style={{ background: "#0C0C0C", color: "#EEEEEE" }} value="quarter">Текущий квартал</option>
              <option style={{ background: "#0C0C0C", color: "#EEEEEE" }} value="halfYear">Текущее полугодие</option>
              <option style={{ background: "#0C0C0C", color: "#EEEEEE" }} value="year">Текущий год</option>
              <option style={{ background: "#0C0C0C", color: "#EEEEEE" }} value="pastYear">Предыдущий год</option>
              <option style={{ background: "#0C0C0C", color: "#EEEEEE" }} value="custom">Произвольный период</option>
            </Select>


          </VStack>
          <VStack flex="1" minW={0}>
            <Text fontSize={"20px"}>Период закрытия счёта</Text>
            <Select
              value={pageStore.order_type_date_end_filter}
              onChange={(e) => {
                pageStore.setOrderTypeDateEndFilter(e.target.value);
                handleEndPeriodChange(e.target.value);
              }}
              width="100%"
            >
              <option style={{ background: "#0C0C0C", color: "#EEEEEE" }} value="none">Без фильтра(все)</option>
              <option style={{ background: "#0C0C0C", color: "#EEEEEE" }} value="fill">Закрыт</option>
              <option style={{ background: "#0C0C0C", color: "#EEEEEE" }} value="empty">Не закрыт</option>
              <option style={{ background: "#0C0C0C", color: "#EEEEEE" }} value="week">Текущая неделя</option>
              <option style={{ background: "#0C0C0C", color: "#EEEEEE" }} value="month">Текущий месяц</option>
              <option style={{ background: "#0C0C0C", color: "#EEEEEE" }} value="pastMonth">Прошлый месяц</option>
              <option style={{ background: "#0C0C0C", color: "#EEEEEE" }} value="quarter">Текущий квартал</option>
              <option style={{ background: "#0C0C0C", color: "#EEEEEE" }} value="halfYear">Текущее полугодие</option>
              <option style={{ background: "#0C0C0C", color: "#EEEEEE" }} value="year">Текущий год</option>
              <option style={{ background: "#0C0C0C", color: "#EEEEEE" }} value="pastYear">Предыдущий год</option>
              <option style={{ background: "#0C0C0C", color: "#EEEEEE" }} value="custom">Произвольный период</option>
            </Select>


          </VStack>
          <VStack flex="1" minW={0}>
            <Text fontSize={"20px"}>Дата готовности груза (прогноз)</Text>
            <Select
              value={pageStore.order_type_cargo_ready_filter}
              onChange={(e) => {
                pageStore.setOrderTypeCargoReadyFilter(e.target.value);
                handleCargoReadyPeriodChange(e.target.value);
              }}
              width="100%"
            >
              {forecastPeriodOptions("Есть дата готовности", "Нет даты готовности")}
            </Select>
          </VStack>
          <VStack flex="1" minW={0}>
            <Text fontSize={"20px"}>Дата у клиента (прогноз)</Text>
            <Select
              value={pageStore.order_type_date_at_client_filter}
              onChange={(e) => {
                pageStore.setOrderTypeDateAtClientFilter(e.target.value);
                handleDateAtClientPeriodChange(e.target.value);
              }}
              width="100%"
            >
              {forecastPeriodOptions("Есть дата у клиента", "Нет даты у клиента")}
            </Select>
          </VStack>
          <VStack flex="1" minW={0}>
            <Text fontSize={"20px"}>Дата отгрузки (факт)</Text>
            <Select
              value={pageStore.order_type_shipment_date_filter}
              onChange={(e) => {
                pageStore.setOrderTypeShipmentDateFilter(e.target.value);
                handleShipmentDatePeriodChange(e.target.value);
              }}
              width="100%"
            >
              {factPeriodOptions("Есть дата отгрузки", "Нет даты отгрузки")}
            </Select>
          </VStack>
          <VStack flex="1" minW={0}>
            <Text fontSize={"20px"}>Дата получения ДЗ (прогноз)</Text>
            <Select
              value={pageStore.order_type_dz_date_filter}
              onChange={(e) => {
                pageStore.setOrderTypeDzDateFilter(e.target.value);
                handleDzPeriodChange(e.target.value);
              }}
              width="100%"
            >
              {forecastPeriodOptions("Есть дата ДЗ", "Нет даты ДЗ")}
            </Select>
          </VStack>
        </HStack>
        {/* Произвольные периоды — отдельным рядом: иначе они сжимали бы фильтры. */}
        <HStack textColor={"white"} width={"100%"} align={"start"} gap={"20px"} flexWrap="wrap">
          {pageStore.order_type_date_start_filter === 'custom' && (
            <VStack>
              <Text fontSize={"20px"}>Дата открытия счёта</Text>
              <HStack>
                <DatePicker
                  selected={pageStore.order_start_open_date_filter ? new Date(pageStore.order_start_open_date_filter) : ''}
                  onChange={(date) => pageStore.setOrderStartOpenDateFilter(date)}
                  width={"15vw"}
                  dateFormat="dd.MM.yyyy"
                  locale={ru}
                  placeholderText="выберете день начала"
                  customInput={
                    <Input
                      style={{
                        border: "none",
                        background: "#333",
                        color: "#EEEEEE",
                        borderRadius: "10px",
                        fontWeight: 600,
                      }}
                    />
                  }
                />
                <DatePicker
                  selected={pageStore.order_end_open_date_filter ? new Date(pageStore.order_end_open_date_filter) : ''}
                  onChange={(date) => pageStore.setOrderEndOpenDateFilter(date)}
                  width={"15vw"}
                  dateFormat="dd.MM.yyyy"
                  locale={ru}
                  placeholderText="выберете день окончания"
                  customInput={
                    <Input
                      style={{
                        border: "none",
                        background: "#333",
                        color: "#EEEEEE",
                        borderRadius: "10px",
                        fontWeight: 600,
                      }}
                    />
                  }
                />
              </HStack>
            </VStack>
          )}
          {pageStore.order_type_date_end_filter === 'custom' && (
            <VStack>
              <Text fontSize={"20px"}>Дата закрытия счёта</Text>
              <HStack>
                <DatePicker
                  selected={pageStore.order_start_close_date_filter ? new Date(pageStore.order_start_close_date_filter) : ''}
                  onChange={(date) => pageStore.setOrderStartCloseDateFilter(date)}
                  width={"15vw"}
                  dateFormat="dd.MM.yyyy"
                  locale={ru}
                  placeholderText="выберете день начала"
                  customInput={
                    <Input
                      style={{
                        border: "none",
                        background: "#333",
                        color: "#EEEEEE",
                        borderRadius: "10px",
                        fontWeight: 600,
                      }}
                    />
                  }
                />
                <DatePicker
                  selected={pageStore.order_end_close_date_filter ? new Date(pageStore.order_end_close_date_filter) : ''}
                  onChange={(date) => pageStore.setOrderEndCloseDateFilter(date)}
                  width={"15vw"}
                  dateFormat="dd.MM.yyyy"
                  locale={ru}
                  placeholderText="выберете день окончания"
                  customInput={
                    <Input
                      style={{
                        border: "none",
                        background: "#333",
                        color: "#EEEEEE",
                        borderRadius: "10px",
                        fontWeight: 600,
                      }}
                    />
                  }
                />
              </HStack>
            </VStack>
          )}
          {pageStore.order_type_cargo_ready_filter === 'custom' && (
            <VStack>
              <Text fontSize={"20px"}>Дата готовности груза</Text>
              <HStack>
                <DatePicker
                  selected={pageStore.order_start_cargo_ready_filter ? new Date(pageStore.order_start_cargo_ready_filter) : ''}
                  onChange={(date) => pageStore.setOrderStartCargoReadyFilter(date)}
                  width={"15vw"}
                  dateFormat="dd.MM.yyyy"
                  locale={ru}
                  placeholderText="выберете день начала"
                  customInput={<Input style={customDatePickerStyle} />}
                />
                <DatePicker
                  selected={pageStore.order_end_cargo_ready_filter ? new Date(pageStore.order_end_cargo_ready_filter) : ''}
                  onChange={(date) => pageStore.setOrderEndCargoReadyFilter(date)}
                  width={"15vw"}
                  dateFormat="dd.MM.yyyy"
                  locale={ru}
                  placeholderText="выберете день окончания"
                  customInput={<Input style={customDatePickerStyle} />}
                />
              </HStack>
            </VStack>
          )}
          {pageStore.order_type_date_at_client_filter === 'custom' && (
            <VStack>
              <Text fontSize={"20px"}>Дата у клиента</Text>
              <HStack>
                <DatePicker
                  selected={pageStore.order_start_date_at_client_filter ? new Date(pageStore.order_start_date_at_client_filter) : ''}
                  onChange={(date) => pageStore.setOrderStartDateAtClientFilter(date)}
                  width={"15vw"}
                  dateFormat="dd.MM.yyyy"
                  locale={ru}
                  placeholderText="выберете день начала"
                  customInput={<Input style={customDatePickerStyle} />}
                />
                <DatePicker
                  selected={pageStore.order_end_date_at_client_filter ? new Date(pageStore.order_end_date_at_client_filter) : ''}
                  onChange={(date) => pageStore.setOrderEndDateAtClientFilter(date)}
                  width={"15vw"}
                  dateFormat="dd.MM.yyyy"
                  locale={ru}
                  placeholderText="выберете день окончания"
                  customInput={<Input style={customDatePickerStyle} />}
                />
              </HStack>
            </VStack>
          )}
          {pageStore.order_type_shipment_date_filter === 'custom' && (
            <VStack>
              <Text fontSize={"20px"}>Дата отгрузки</Text>
              <HStack>
                <DatePicker
                  selected={pageStore.order_start_shipment_date_filter ? new Date(pageStore.order_start_shipment_date_filter) : ''}
                  onChange={(date) => pageStore.setOrderStartShipmentDateFilter(date)}
                  width={"15vw"}
                  dateFormat="dd.MM.yyyy"
                  locale={ru}
                  placeholderText="выберете день начала"
                  customInput={<Input style={customDatePickerStyle} />}
                />
                <DatePicker
                  selected={pageStore.order_end_shipment_date_filter ? new Date(pageStore.order_end_shipment_date_filter) : ''}
                  onChange={(date) => pageStore.setOrderEndShipmentDateFilter(date)}
                  width={"15vw"}
                  dateFormat="dd.MM.yyyy"
                  locale={ru}
                  placeholderText="выберете день окончания"
                  customInput={<Input style={customDatePickerStyle} />}
                />
              </HStack>
            </VStack>
          )}
          {pageStore.order_type_dz_date_filter === 'custom' && (
            <VStack>
              <Text fontSize={"20px"}>Дата получения ДЗ</Text>
              <HStack>
                <DatePicker
                  selected={pageStore.order_start_dz_date_filter ? new Date(pageStore.order_start_dz_date_filter) : ''}
                  onChange={(date) => pageStore.setOrderStartDzDateFilter(date)}
                  width={"15vw"}
                  dateFormat="dd.MM.yyyy"
                  locale={ru}
                  placeholderText="выберете день начала"
                  customInput={<Input style={customDatePickerStyle} />}
                />
                <DatePicker
                  selected={pageStore.order_end_dz_date_filter ? new Date(pageStore.order_end_dz_date_filter) : ''}
                  onChange={(date) => pageStore.setOrderEndDzDateFilter(date)}
                  width={"15vw"}
                  dateFormat="dd.MM.yyyy"
                  locale={ru}
                  placeholderText="выберете день окончания"
                  customInput={<Input style={customDatePickerStyle} />}
                />
              </HStack>
            </VStack>
          )}
        </HStack>
        <HStack align="center" gap={3} flexWrap="wrap" textColor="white">
          <ColumnManager
            report="debitor"
            columns={pageStore.columnConfigDebitor}
            onChange={(newColumns) => pageStore.setColumnConfigDebitor(newColumns)}
            introducedColumnKeys={[INN_COLUMN_KEY]}
          />
          <Button size="sm" colorScheme="green" onClick={selectCompactView}>
            Компактный вид
          </Button>
        </HStack>
        </VStack>
      </Collapse>
      <HStack
        justify="space-between"
        mb={4}
        borderBottom="1px solid #444"
        pb={3}
        w="full"
      >
        <Button
          size="sm"
          bg="#333"
          color="white"
          onClick={() => pageStore.setOpenFilters(!pageStore.open_filter)}
          _hover={{ bg: "#444" }}
          leftIcon={pageStore.open_filter ? <span>−</span> : <span>+</span>}
        >
          {pageStore.open_filter ? "Скрыть фильтры" : "Открыть фильтры"}
        </Button>
        <HStack spacing={4} flex="1">
          <Text fontSize="12px" fontWeight="bold" color="gray.400" whiteSpace="nowrap">
            Высота строк: {pageStore.rowHeight}
          </Text>

          <HStack spacing={2} flex="1" maxW="300px">
            <Button
              size="xs"
              onClick={() => pageStore.setRowHeight(Math.max(0, pageStore.rowHeight - 1))}
            >
              -
            </Button>

            <Slider
              id="row-padding-slider"
              min={0}
              max={10}
              step={1}
              value={pageStore.rowHeight}
              onChange={(v) => pageStore.setRowHeight(v)}
              colorScheme="green"
            >
              <SliderTrack bg="#444">
                <SliderFilledTrack />
              </SliderTrack>
              <SliderThumb boxSize={3} />
            </Slider>

            <Button
              size="xs"
              onClick={() => pageStore.setRowHeight(Math.min(10, pageStore.rowHeight + 1))}
            >
              +
            </Button>
          </HStack>
          <Text
            fontSize="12px"
            color="gray.400"
            fontWeight="600"
            whiteSpace="nowrap"
          >
            Время запроса: {pageStore.request_time.toFixed(2)} сек.
          </Text>
        </HStack>
      </HStack>
    </>
  )
})

export default DateFilters
